
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { Column } from "@/types/array.types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const PAGE_SIZE = 15;
const supabase = useSupabaseClient();

export type FieldType =
  | "text"
  | "number"
  | "date"
  | "boolean";

export type SortOption = {
    key: string;
    label: string;
    by: "croissant" | "decroissant"
  };

export type TagType = {
    key: string,
    value: string,
    label: string
}

export type StatsType = {
    total_assets: number,
    total_value: number,
    last_update: string;
}

export const getRole = async () => {
    const { data, error } = await supabase.rpc("get_my_role")
    if (error) {
        console.error(error);
        return;

    }

    return { data }
};

export const getFilterData = async (table: string, column: string) => {
  const { data, error } = await supabase.rpc(
    "get_distinct_filter_values",
    {
      table_name: table,
      column_name: column,
    }
  );

  if (error) {
    console.error(error);
    return [];
  }

  return data.map((item) => item.value);
};

export const getAllData = async (table: string) => {
    const { data, error } = await supabase
    .from(table)
    .select("*")
  

      if (error) {
        console.error(error);
        return;
      }

    return { data, error }
}

export const getData = async (
    page: number,
    table: string,
    sortOption?: SortOption,
    tags?: TagType[]
  ) => {
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    const groupedTags = tags.reduce<Record<string, string[]>>((acc, tag) => {
      if (!acc[tag.key]) {
        acc[tag.key] = [];
      }
    
      acc[tag.key].push(tag.value);
    
      return acc;
    }, {});

    let query = supabase
      .from(table)
      .select("*, documents ( storage_path )", 
        { count: "exact" });
    if(tags && tags.length>0){
        Object.entries(groupedTags).forEach(([key, values]) => {
          if (values.length === 1) {
            const value = values[0];
        
            if (value === "Oui") {
              query = query.eq(key, true);
            } else if (value === "Non") {
              query = query.eq(key, false);
            } else {
              query = query.eq(key, value);
            }
          } else {
            query = query.in(key, values);
          }
        });
      }
    if (sortOption) {
        query = query.order(sortOption.key, {
          ascending: sortOption.by === "croissant",
        });
      }
    
      const { data, count, error } = await query.range(from, to);

      if (error) {
        console.error(error);
        return;
      }

      const dataWithImages = await Promise.all(
        data.map(async (item) => {
          if (!item.documents?.storage_path) {
            return item;
          }

          const { data: signedUrl } = await supabase.storage
            .from("documents")
            .createSignedUrl(item.documents.storage_path, 3600, {transform: {
              width: 120,
              height: 120,
              resize: "cover",
              quality: 50,
        }});

          return {
            ...item,
            imageUrl: signedUrl?.signedUrl,
          };
        })
      );

      return { 
        data: dataWithImages, 
        count, 
        error 
      };
    };

export const getStats = async (view: string) => {
    const { data, error } = await supabase
    .from(view)
    .select()
    .single();

    if (error) {
        console.error(error);
        return;

    }
    return { data, error }
}
  

export const onDelete = async (id: string, table: string) => {
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) {
      console.error(error);
      return;
    }  
};

export const onCreate = async <T extends Record<string, any>>(id: string, values: T, table: string) => {
    const data = {...values, created_at: new Date(), created_by: id};
    const { error } = await supabase.from(table).insert(data);
    if (error) {
        console.error(error);
        return;
    }    
};

export const onUpdate = async <T extends Record<string, any>>(user_id: string, values: T, table: string, id: string) => {
    const data = {...values, updated_by: user_id};
  
    const { error } = await supabase.from(table).update(data).match({id});
    if (error) {
        console.error(error);
        return;
    }
};

export const onUploadPhoto = async (photo: File) => {
  const fileName = `${crypto.randomUUID()}-${photo.name}`;
 
  const { data: file, error } = await supabase.storage
    .from("documents")
    .upload(fileName, photo);

    if (error) {
      console.error(error);
      return;
    }

  const { data: document } = await supabase
    .from("documents")
    .insert({
      storage_path: file.path,
      file_name: photo.name,
    })
    .select()
    .single();

  return { document } ;
};

export const getPreview = async (photoId: string) => {
  
    const { data, error } = await supabase
    .from("art")
    .select(`
      *,
      documents (
        storage_path
      )
    `)
    .eq("id", photoId)
    .single();

    const { data: signedUrl } = await supabase.storage
    .from("documents")
    .createSignedUrl(
      data.documents.storage_path,
      3600
    );

    return { signedUrl, error } ;
};

export const onExportData = async <T extends Record<string, any>>(
  columnsExport: Column<T>[], 
  columnsKeys: string[], 
  table: string,
  sortOption?: SortOption,
  tags?: TagType[],
  name?: string, 
) => { 

  const selectedColumns = columnsExport.filter(column =>
    columnsKeys.includes(column.key)
  );

  const headers = selectedColumns.map(c => c.label);

  const groupedTags = tags?.reduce<Record<string, string[]>>((acc, tag) => {
    if (!acc[tag.key]) {
      acc[tag.key] = [];
    }
  
    acc[tag.key].push(tag.value);
  
    return acc;
  }, {});



    let query = supabase
    .from(table)
    .select(`${columnsKeys.join(", ")} ${columnsKeys.includes("document_id") ? ", documents(storage_path)" : ""}`, 
      { count: "exact" });

      if(tags && tags.length>0)
      {

          Object.entries(groupedTags).forEach(([key, values]) => {
            if (values.length === 1) {
              const value = values[0];
          
              if (value === "Oui") {
                query = query.eq(key, true);
              } else if (value === "Non") {
                query = query.eq(key, false);
              } else {
                query = query.eq(key, value);
              }
            } else {
              query = query.in(key, values);
            }
          });
        }
      if (sortOption) {
          query = query.order(sortOption.key, {
            ascending: sortOption.by === "croissant",
          });
        }
        const { error, data } = await query as {
          data: T[];
          error: any;
        }
        const doc = new jsPDF();

        if(error) {
          console.log(error);
          return
        }

    const imageToBase64 = async (url: string) => {
      const response = await fetch(url);
      const blob = await response.blob();
    
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    };

    const dataWithImages = await Promise.all(
      data.map(async (item) => {
    
        if (!item.documents?.storage_path) {
          return item;
        }
    
        const { data: signedUrl } = await supabase.storage
          .from("documents")
          .createSignedUrl(item.documents.storage_path, 3600);
    
        return {
          ...item,
          imageBase64: await imageToBase64(signedUrl.signedUrl),
        };
      })
    );


    const body = dataWithImages.map(row =>
      selectedColumns.map(column => {
        if (column.key === "document_id") {
          return "";
        }
    
        const value = row[column.key as keyof typeof row];
    
        return column.render
          ? String(column.render(row as T))
          : String(value ?? "");
      })
    );

    doc.text("Patrimoine", 15, 20);
    autoTable(doc, {
        head: [headers],
        body,
        styles: {
          minCellHeight: 18,
        },

        didDrawCell: (hook) => {
          if (hook.section !== "body") return;

          const column = selectedColumns[hook.column.index];

          if (!column || column.key !== "document_id") return;

          const row = dataWithImages[hook.row.index];

          if (!row) return;

          if (!row.imageBase64) return;

          doc.addImage(
            row.imageBase64,
            "JPEG",
            hook.cell.x + 1,
            hook.cell.y + 1,
            16,
            16
          );
        }
    });

   doc.save(name ? `${name}.pdf` : "rapport.pdf");
};
